import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';

const SALES = ['Under $5M', '$5M-$10M', '$10M-$20M', '$20M+', 'Not A Licensed Agent'];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  annualSales: '',
  introducedBy: '',
  consent: false,
};

// Validation helpers
function validateForm(form) {
  if (!form.firstName || !form.lastName || !form.email || !form.phone) {
    return 'Please complete required fields.';
  }
  if (!form.consent) {
    return 'Please accept the consent disclaimer.';
  }
  return null;
}

function toApiPayload(form) {
  return {
    first_name: form.firstName,
    last_name: form.lastName,
    middle_name: null,
    email: form.email,
    phone: form.phone,
    annual_sales: form.annualSales || null,
    introduced_by: form.introducedBy || null,
    consent: form.consent,
  };
}

function extractErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) return detail[0]?.msg || 'Submission failed.';
  return detail || 'Submission failed. Please try again.';
}

export default function ContactForm() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const error = validateForm(form);
    if (error) {
      toast.error(error);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/introductions`, toApiPayload(form));
      toast.success('Thank You — Your inquiry has been received. We look forward to a confidential conversation.', { duration: 6000 });
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'w-full bg-transparent border-0 border-b border-white/30 rounded-none px-0 py-3 text-white placeholder:text-white/50 focus-visible:ring-0 focus-visible:border-[#b39869] transition-colors';

  return (
    <section id="contact" className="bg-[#0b0b0b] text-white py-24 md:py-32 px-6">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-14">
          <div className="inline-block mb-6 pb-1 border-b border-[#b39869]">
            <span className="tracking-[0.35em] text-[11px] text-[#b39869] uppercase">Contact</span>
          </div>
          <h2
            className="text-4xl md:text-5xl mb-5 leading-tight"
            style={{ fontFamily: 'Playfair Display, serif', fontWeight: 400 }}
          >
            Begin Your Introduction
          </h2>
          <p className="text-white/75 text-sm md:text-base">
            For those aligned with excellence, discretion, and an elevated standard.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-8">
          <div className="grid md:grid-cols-2 gap-8">
            <Input
              placeholder="First Name *"
              value={form.firstName}
              onChange={(e) => update('firstName', e.target.value)}
              className={inputCls}
            />
            <Input
              placeholder="Last Name *"
              value={form.lastName}
              onChange={(e) => update('lastName', e.target.value)}
              className={inputCls}
            />
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <Input
              type="email"
              placeholder="Email *"
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
              className={inputCls}
            />
            <Input
              type="tel"
              placeholder="Phone *"
              value={form.phone}
              onChange={(e) => update('phone', e.target.value)}
              className={inputCls}
            />
          </div>

          <Select value={form.annualSales} onValueChange={(v) => update('annualSales', v)}>
            <SelectTrigger className="w-full bg-transparent border-0 border-b border-white/30 rounded-none px-0 py-3 text-white focus:ring-0 focus-visible:ring-0 h-auto data-[placeholder]:text-white/50">
              <SelectValue placeholder="Annual Sales" />
            </SelectTrigger>
            <SelectContent className="bg-[#111] text-white border-white/20">
              {SALES.map((s) => (
                <SelectItem key={s} value={s} className="focus:bg-white/10 focus:text-white">
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            placeholder="Introduced By"
            value={form.introducedBy}
            onChange={(e) => update('introducedBy', e.target.value)}
            className={inputCls}
          />

          <div className="flex items-start gap-3 pt-2">
            <Checkbox
              id="consent"
              checked={form.consent}
              onCheckedChange={(v) => update('consent', !!v)}
              className="mt-1 border-white/40 data-[state=checked]:bg-[#b39869] data-[state=checked]:border-[#b39869]"
            />
            <label htmlFor="consent" className="text-xs text-white/70 leading-relaxed">
              I agree to be contacted by Hunt Global Collective via call, email, and text for real estate services.
              To opt out, reply ‘stop’ at any time or reply ‘help’ for assistance. You can also click the unsubscribe
              link in emails. Message and data rates may apply.
            </label>
          </div>

          <div className="pt-4 text-center">
            <button
              type="submit"
              disabled={submitting}
              className="bg-[#b39869] hover:bg-[#9d8358] disabled:opacity-60 transition-colors duration-300 text-white uppercase tracking-[0.28em] text-xs px-12 py-4"
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
